import React, { useState } from "react";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { detectPersonCollision, resolvePersonCode } from "../utils/persons";
import { activeRoleLocations, suggestSalesCode, personCollisionMessage, pickableBranchesForPerson } from "../utils/salespeople";
import PersonCollisionModal from "./PersonCollisionModal";

const CODE_RE = /^[A-Z0-9]{1,4}$/;
const PUSAT_VALUE = "pusat"; // <select> sentinel for branchId: null — see the branch <select> below
const NOT_CHOSEN = ""; // branchId while no location has been picked yet (silent-defaults fix)

/**
 * components/SalespersonFormModal.js
 * The shared "create a role" flow (Person/Role restructure, Stage 3 batch 3a, Part A) — the
 * ONE place either creation path opens: `KelolaSalesModal`'s "+ Tambah Sales" and
 * `SalespersonPicker`'s inline "+ Tambah sales baru…" (inside `TransactionFormModal`) both
 * render this same component now, instead of creating anything themselves.
 *
 * **Two stages, not one — this is the real design difference from `ContactPicker`/
 * `SalespersonPicker`'s own search-or-create pattern, which this otherwise follows closely.**
 * Those two pickers create ONE flat record from typed text alone. A role is TWO records —
 * WHO (a person, who might already exist under this exact name) and WHERE (branch + code) —
 * and the two questions can't be collapsed into one "type a name, hit enter" step: two
 * different real people can share a name, so typing "Abun" is never enough on its own to know
 * whether this is a brand-new Abun or an existing one just picking up a second role. So this
 * component is a small internal state machine (`step`) instead of a single combobox:
 *   1. "person" — a plain search box over `persons` (never a floating combobox menu — this is
 *      a full modal, not an inline picker, so the results render directly in the body). Every
 *      match shows `{name} — {where they currently work}` (via `activeRoleLocations`) so two
 *      same-named people are told apart on sight, plus a "+ Tambah '{query}' sebagai orang
 *      baru" row — ALWAYS offered whenever there's typed text, even when an exact name match
 *      already exists (unlike `ContactPicker`'s own `exact` suppression: contacts dedupe by
 *      name, persons deliberately never do — see `persons.js`'s own header comment). Picking an
 *      existing person or the "orang baru" row both advance to step 2, carrying who was chosen.
 *   2. "role" — **Kode only appears for a BRAND-NEW person (Stage 4)** — pre-filled via
 *      `suggestSalesCode` against every PERSON's existing code, still freely editable, required
 *      before Simpan enables. **For an EXISTING person, there is no Kode field at all** — their
 *      code already exists, on the person, and this step shows it read-only instead ("Kode: BU")
 *      so the human confirms who they're picking up a role for without being asked to invent
 *      (or worse, accidentally change) a code that already identifies them everywhere they sell.
 *      Adding a second role for an existing person therefore only ever asks for Cabang (starts on
 *      the caller's `initialBranchId` when given, else on "— pilih lokasi —", see below). Simpan runs `detectPersonCollision`
 *      before ever calling `onSubmit` — see Part B below.
 * "← Kembali" on step 2 returns to step 1 without losing the typed search query.
 *
 * **Part B — the collision guard.** Simpan on step "role" builds the SAME `change` shape
 * `detectPersonCollision` expects (`{kind:"add-person", name, branchId}` for a brand-new
 * person, `{kind:"add-role", personId, branchId}` for an existing one) and checks it FIRST. A
 * non-ambiguous result submits immediately. An ambiguous one opens `PersonCollisionModal`
 * (step "collision") instead of submitting — "Pakai yang sudah ada" re-targets `chosenPersonId`
 * at `result.existing.id` and submits immediately (skipping back through step "role" — code/
 * branch already typed); "Lanjutkan" collects a required note (inside that same dialog, not a
 * redirect) and submits with it attached to whichever person ends up being used.
 *
 * **`branchId: null` (Pusat) IS a real, pickable option here** — the branch `<select>` below
 * includes it (`PUSAT_VALUE` sentinel), unlike the EXISTING `KelolaSalesModal` edit-row's own
 * branch select, which has never offered it (see that component's own header comment for what
 * this means for a role that already has `branchId: null` today: it exists and works fine
 * everywhere a role is USED, but is completely invisible in `KelolaSalesModal`'s own
 * branch-grouped roster — a real, pre-existing gap this batch does not fix, since fixing THAT
 * display is the Pusat/Cabang-split work explicitly deferred to Stage 4). Perpetuating the same
 * "can't even create one" gap in a BRAND NEW form would have been a quieter version of the
 * exact problem this whole restructure exists to close, so this form doesn't repeat it.
 *
 * Props: `{ persons=[], salespeople=[], branches=[], initialQuery="", initialBranchId (undefined =
 *          nothing pre-picked, null = Pusat, a branch id = that branch),
 *          initialPersonId=null, initialPersonName="",
 *          onSubmit(fields) -> newRoleId|null, onClose }`. `fields` sent to `onSubmit`:
 *   `{ personId, branchId }` (existing person — `name`/`code`/`note` omitted entirely, Stage 4:
 *   the person already has a code, and this flow never changes an existing person's code) or
 *   `{ name, note, code, branchId }` (new person — `personId` omitted, `code` required since a
 *   brand-new person needs one). `note` is present only when the collision guard's "Lanjutkan"
 *   path was taken; otherwise omitted entirely, same "absent means don't touch it" convention
 *   `updatePerson`'s own `fields.note` already uses.
 *
 * **`initialPersonId`/`initialPersonName` (Stage 3 batch 3b, Part E)**: when the caller already
 * knows exactly which person this role is for — `KelolaSalesModal`'s own "Orang Tanpa Peran"
 * section's "+ Tambah Peran" button, opened directly from an existing person's own row — the
 * modal starts on step "role" already, skipping the search step entirely (there is nothing to
 * search for; the person is already chosen). Both existing callers (`KelolaSalesModal`'s
 * "+ Tambah Sales", the transaction form's quick-add) omit these, so step "person" remains their
 * unchanged starting point.
 */
export default function SalespersonFormModal({
  persons = [], salespeople = [], branches = [],
  initialQuery = "", initialBranchId,
  initialPersonId = null, initialPersonName = "",
  onSubmit, onClose,
}) {
  const [step, setStep] = useState(initialPersonId ? "role" : "person"); // "person" | "role" | "collision"
  const [query, setQuery] = useState(initialQuery);
  const [chosenPersonId, setChosenPersonId] = useState(initialPersonId); // null while on step "person"; null on step "role" means "new person"
  const [chosenName, setChosenName] = useState(initialPersonName);
  // Shared by the lazy `branchId` initializer (the `initialPersonId` skip-straight-to-"role"
  // case) and `advanceToRole` (the normal case), so the two can never resolve the starting
  // branch differently.
  // Silent-defaults fix (2026-10-12): the caller's own location is the ONLY starting value —
  // `null` = Pusat (a Pusat sale), a real branch id = that branch (a Cabang sale, a branch-scoped
  // Kelola Sales view), `undefined` = no starting value at all: the select shows "— pilih
  // lokasi —" and Tambah waits for a deliberate pick. It used to fall back to
  // `defaultBranchId` (MMC), which on a Pusat sale created an MMC role nobody chose.
  const resolveStartBranch = () => {
    if (initialBranchId === null) return null;
    if (initialBranchId && branches.some((b) => b.id === initialBranchId)) return initialBranchId;
    return NOT_CHOSEN;
  };

  // Stage 5 (Part B, one role per person per location): the branch select must never even
  // OFFER a location the CHOSEN person already holds an ACTIVE role at — the addSalesperson
  // write would refuse it outright, and letting the picker land there silently (e.g. because
  // the scoped default happens to coincide with it — "adding a role from a branch-scoped view
  // where the branch defaults to the duplicate case") would surface only as a generic post-
  // submit error. `"move"` mode is the right shape here, not `"edit"`: an INACTIVE role at a
  // location is a perfectly good pick for CREATION (addSalesperson reactivates it — a genuine,
  // useful outcome, not something to hide), so it's offered, flagged. For a brand-new person
  // (`personId` null), nothing has ever been created yet, so every branch is available — the
  // filter is a no-op there, `excludeRoleId` needs no value since no role is "being edited."
  const branchOptionsFor = (personId) => pickableBranchesForPerson(persons, salespeople, branches, personId, null, "move");

  // The starting branch: the caller's own location when it's actually available for THIS
  // person, else NOT_CHOSEN (the person picks); `undefined` only when there is truly nothing
  // left to offer (the person already holds an active role at every location, Pusat included).
  const resolveStartBranchFor = (personId) => {
    const options = branchOptionsFor(personId);
    if (options.length === 0) return undefined; // nothing pickable at all
    // NOT `options[0]?.id ?? undefined` — Pusat's own real id IS `null`, and `??` would
    // wrongly treat that legitimate value as "missing" and collapse it to `undefined`.
    const preferred = resolveStartBranch();
    // Never "the first option" either — that's the same silent pick; an unavailable preferred
    // location just means the person chooses.
    return preferred !== NOT_CHOSEN && options.some((o) => o.id === preferred) ? preferred : NOT_CHOSEN;
  };

  // Stage 4: `code` is only ever asked/typed for a BRAND-NEW person (`chosenPersonId === null`)
  // — an EXISTING person's code already exists, on the person, and is never re-asked here. Every
  // caller of THIS component's own `initialPersonId` shortcut always names an EXISTING person, so
  // there is nothing to suggest a code for at mount time either.
  const [code, setCode] = useState("");
  const [branchId, setBranchId] = useState(() => (initialPersonId ? resolveStartBranchFor(initialPersonId) : null));
  const [collision, setCollision] = useState(null); // { kind, result } while step === "collision"
  const [submitError, setSubmitError] = useState(null);

  useModalEsc(onClose);

  const trimmedQuery = query.trim();
  const matches = trimmedQuery
    ? persons.filter((p) => p.name.toLowerCase().includes(trimmedQuery.toLowerCase()))
    : persons;

  const advanceToRole = (personId, name) => {
    setChosenPersonId(personId);
    setChosenName(name);
    // A brand-new person (personId null) needs a fresh code suggested against every PERSON's
    // existing code (Stage 4 — codes are unique per person, not per role); an existing person
    // already has one, so there's nothing to suggest or show an input for.
    setCode(personId ? "" : suggestSalesCode(name, persons.map((p) => p.code)));
    setBranchId(resolveStartBranchFor(personId));
    setSubmitError(null);
    setStep("role");
  };

  const isNewPerson = !chosenPersonId;
  const codeTrimmed = code.trim().toUpperCase();
  const codeUsed = persons.some((p) => p.code === codeTrimmed);
  // Disabled-control sweep (manual-testing report): a blank code used to fall through to the
  // SAME "" this returns for an existing person (where the field doesn't even apply) — so Tambah
  // sat disabled with nothing on screen explaining why. Blank now gets its own real message
  // through this exact same `{codeError && ...}` render path (no new JSX needed) — only the
  // genuinely-not-applicable case (an existing person, no code field shown at all) still returns
  // "".
  const codeError = !isNewPerson ? "" : !codeTrimmed ? "Wajib diisi." : !CODE_RE.test(codeTrimmed) ? "Kode harus 1-4 huruf/angka." :
    codeUsed ? "Kode sudah dipakai." : "";
  // A branch must actually be pickable — `branchId` stays `undefined` (never a valid select
  // value) when `resolveStartBranchFor` found nowhere left to offer, e.g. the chosen person
  // already holds an active role at every location, Pusat included. An existing person
  // otherwise needs only a real branch pick; a brand-new person also needs a valid,
  // non-colliding code.
  const hasBranchToOffer = branchId !== undefined;
  const branchChosen = branchId !== NOT_CHOSEN;
  const roleValid = hasBranchToOffer && branchChosen && (isNewPerson ? (!!codeTrimmed && !codeError) : true);

  // `personIdOverride` (Part B's "Pakai yang sudah ada" path) exists because
  // `setChosenPersonId(...)` immediately followed by `commit()` in the SAME handler would
  // otherwise read the OLD `chosenPersonId` from this render's closure — React state updates
  // aren't visible until the next render, so committing off `chosenPersonId` right after
  // setting it is a stale read, not a real retarget.
  const commit = (extra, personIdOverride) => {
    const pid = personIdOverride !== undefined ? personIdOverride : chosenPersonId;
    if (personIdOverride !== undefined) setChosenPersonId(personIdOverride); // keep state in sync for a failed-submit fallback to step "role"
    // Stage 4: an EXISTING person (pid truthy) never sends `code` at all — their code is fixed,
    // never re-asked or re-sent here; only a brand-new person's `fields` carries one.
    const fields = pid
      ? { personId: pid, branchId, ...extra }
      : { name: chosenName, code: codeTrimmed, branchId, ...extra };
    const newId = onSubmit(fields);
    if (newId) {
      onClose();
    } else {
      setSubmitError("Sales tidak dibuat — kode mungkin sudah dipakai, atau data tidak valid.");
      setStep("role");
    }
  };

  const handleSimpanRole = () => {
    if (!roleValid) return;
    const kind = chosenPersonId ? "add-role" : "add-person";
    const change = kind === "add-role"
      ? { kind, personId: chosenPersonId, branchId }
      : { kind, name: chosenName, branchId };
    const result = detectPersonCollision({ persons, salespeople }, change);
    if (result.ambiguous) {
      setCollision({ kind, result });
      setStep("collision");
      return;
    }
    commit({});
  };

  if (step === "collision") {
    const { kind, result } = collision;
    const newBranchName = branchId === null ? "Pusat" : (branches.find((b) => b.id === branchId)?.name || "Cabang");
    return (
      <PersonCollisionModal
        result={result}
        message={personCollisionMessage(kind, result, branches, newBranchName)}
        canUseExisting
        // The CHANGED person's own current note (blank for a brand-new person, who has none
        // yet) — never the OTHER, already-real person's note (result.existing), which would
        // read as this new/changed person already having someone else's distinguishing text.
        initialNote={chosenPersonId ? (persons.find((p) => p.id === chosenPersonId)?.note || "") : ""}
        onUseExisting={() => commit({}, result.existing.id)}
        onContinue={(note) => commit({ note })}
        onCancel={() => setStep("role")}
      />
    );
  }

  if (step === "role") {
    const existingLocations = chosenPersonId ? activeRoleLocations(persons, salespeople, branches, chosenPersonId) : null;
    const branchOptions = branchOptionsFor(chosenPersonId);
    // Part C: the chosen person's own note, shown here so a human picking up a SECOND role for
    // an already-disambiguated person still sees why they were told apart from anyone sharing
    // their name — blank for a brand-new person (nothing to show yet) and never the note of
    // some other, unrelated person.
    const chosenNote = chosenPersonId ? (persons.find((p) => p.id === chosenPersonId)?.note || "") : "";
    return (
      <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
        <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
          <h2 className="modal-title">Tambah Sales</h2>
          <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
            Untuk: <b>{chosenName}</b>{" "}
            {chosenNote && <span style={{ color: "var(--muted)" }}>— {chosenNote}{" "}</span>}
            {chosenPersonId
              ? `— sudah ada${existingLocations.length ? ` (${existingLocations.join(", ")})` : " (belum ada cabang aktif)"}`
              : "— orang baru"}
          </div>
          {isNewPerson ? (
            <div className="form-row">
              <label className="form-label" htmlFor="sales-form-kode">Kode (1-4 huruf/angka)</label>
              <input id="sales-form-kode" className="form-input mono" value={code} autoFocus
                onChange={(e) => setCode(e.target.value.toUpperCase())} />
              {codeError && <div className="form-hint" style={{ color: "var(--red)" }}>{codeError}</div>}
            </div>
          ) : (
            // Stage 4: an existing person's code already exists, on the person — shown
            // read-only rather than re-asked, so adding a second role only ever asks for
            // the location below.
            <div className="form-row">
              <label className="form-label">Kode</label>
              <div><span className="chip chip--muted mono">{resolvePersonCode(persons, chosenPersonId) || "—"}</span></div>
            </div>
          )}
          <div className="form-row">
            <label className="form-label" htmlFor="sales-form-cabang">Cabang</label>
            {/* Stage 5 (Part B): options are the person's own PICKABLE branches only — a
               location they already hold an ACTIVE role at is never offered at all (refused,
               not something to explain after Tambah is clicked); one they hold an INACTIVE
               role at IS offered, flagged, since picking it reactivates that old role. */}
            {branchOptions.length === 0 ? (
              <div className="form-hint" style={{ color: "var(--red)" }}>
                {chosenName} sudah punya peran aktif di setiap lokasi — tidak ada cabang yang bisa dipilih.
              </div>
            ) : (
              <select id="sales-form-cabang" className="form-select" value={branchId === null ? PUSAT_VALUE : (branchId ?? NOT_CHOSEN)} autoFocus={!isNewPerson}
                onChange={(e) => setBranchId(e.target.value === PUSAT_VALUE ? null : e.target.value)}>
                {!branchChosen && <option value={NOT_CHOSEN} disabled>— pilih lokasi —</option>}
                {branchOptions.map((opt) => (
                  <option key={opt.id === null ? PUSAT_VALUE : opt.id} value={opt.id === null ? PUSAT_VALUE : opt.id}>
                    {opt.label}{opt.willReactivate ? " (aktifkan kembali)" : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          {hasBranchToOffer && !branchChosen && branchOptions.length > 0 && (
            <div className="form-hint sales-form-pick-location">Pilih lokasi tempat orang ini berjualan.</div>
          )}
          {submitError && <div className="form-hint" style={{ color: "var(--red)" }}>{submitError}</div>}
          <div className="modal-actions">
            <button type="button" className="btn" onClick={() => setStep("person")}>← Kembali</button>
            <button type="button" className="btn" onClick={onClose}>Batal</button>
            <button type="button" className="btn btn-primary" disabled={!roleValid} onClick={handleSimpanRole}>Tambah</button>
          </div>
        </div>
      </div>
    );
  }

  // step === "person"
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box" onMouseDown={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Tambah Sales</h2>
        <div className="page-sub" style={{ marginBottom: "var(--gap-lg)" }}>
          Cari orangnya dulu — dua orang bisa punya nama yang sama, jadi setiap hasil menunjukkan
          di mana mereka sudah bekerja supaya bisa dibedakan.
        </div>
        <div className="form-row">
          <label className="form-label" htmlFor="sales-form-query">Nama</label>
          <input id="sales-form-query" className="form-input" value={query} autoFocus placeholder="Cari nama…"
            onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="table-wrap" style={{ maxHeight: 260, overflowY: "auto" }}>
          {matches.length === 0 && !trimmedQuery && <div className="empty-state">Belum ada orang.</div>}
          {matches.map((p) => {
            const locations = activeRoleLocations(persons, salespeople, branches, p.id);
            return (
              <div key={p.id} className="picker__row" style={{ cursor: "default", justifyContent: "space-between" }}>
                <span>
                  <span className="picker__name">{p.name}</span>
                  {p.note && <span className="picker__meta"> ({p.note})</span>}
                  <span className="picker__meta"> — {locations.length ? locations.join(", ") : "belum ada cabang aktif"}</span>
                </span>
                <button type="button" className="btn btn-sm" onClick={() => advanceToRole(p.id, p.name)}>pilih</button>
              </div>
            );
          })}
          {trimmedQuery && (
            <div style={{ padding: "8px 4px" }}>
              <button type="button" className="btn btn-sm btn-soft" onClick={() => advanceToRole(null, trimmedQuery)}>
                + Tambah "{trimmedQuery}" sebagai orang baru
              </button>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn" onClick={onClose}>Batal</button>
        </div>
      </div>
    </div>
  );
}
